import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Badge = styled.div`
  padding: 6px 12px;
  font-size: 10px;
  font-family: 'Courier New', monospace;
  color: #c0cfe8;
  line-height: 1.5;
  letter-spacing: 0.02em;
`;

const CACHE_KEY = 'drschmoeai_version';
const CACHE_TS_KEY = 'drschmoeai_version_ts';
const CACHE_TTL = 10 * 60 * 1000; // 10 min

interface VersionInfo {
  version: string;
  commit: string;
  build_date: string;
  is_dev: boolean;
}

const AppVersionBadge: React.FC = () => {
  const [info, setInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    const isLocal = process.env.REACT_APP_DEPLOY_MODE !== 'PROD' && process.env.REACT_APP_DEPLOY_MODE !== 'DEV';
    if (isLocal) {
      setInfo({ version: 'local-dev', commit: 'local-dev', build_date: 'local-dev', is_dev: true });
      return;
    }

    const cached = sessionStorage.getItem(CACHE_KEY);
    const ts = sessionStorage.getItem(CACHE_TS_KEY);
    if (cached && ts && Date.now() - parseInt(ts) < CACHE_TTL) {
      try { setInfo(JSON.parse(cached)); return; } catch {}
    }

    const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    fetch(`${baseUrl}/health/version`, { signal: AbortSignal.timeout(3000) })
      .then((r) => r.json())
      .then((data: VersionInfo) => {
        setInfo(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        sessionStorage.setItem(CACHE_TS_KEY, Date.now().toString());
      })
      .catch(() => {
        setInfo({ version: 'unknown', commit: 'unknown', build_date: 'unknown', is_dev: false });
      });
  }, []);

  if (!info) return null;

  if (info.is_dev) {
    return <Badge>local-dev</Badge>;
  }

  const displayVersion = info.version.startsWith('dev-')
    ? info.version
    : `v${info.version}`;

  return (
    <Badge title={`Built ${info.build_date}`}>
      {displayVersion} · {info.commit.substring(0, 8)}
    </Badge>
  );
};

export default AppVersionBadge;
